import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { CompanyContextService } from '../../../services/company-context';
import { CompanyMember, CompanyRole, CompanyService } from '../../../services/api/company';
import { ConfirmDialogService } from '../../../services/confirm-dialog';

/**
 * Сотрудники компании. Администратор компании сам добавляет сотрудника: имя, email, пароль и роль.
 * Сотрудник входит в систему своим email и паролем (обычная форма входа) и работает в рамках своей компании и роли.
 */
@Component({
  selector: 'app-company-members',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './company-members.html',
  styleUrl: './company-members.scss'
})
export class CompanyMembersComponent implements OnInit, OnDestroy {
  readonly roles: CompanyRole[] = ['COMPANY_ADMIN', 'COMPANY_MANAGER', 'COMPANY_OPERATOR'];

  members: CompanyMember[] = [];
  loading = true;
  /** Раздел выключен на сервере или у пользователя нет права. */
  unavailable = false;
  error: string | null = null;
  success: string | null = null;

  showForm = false;
  /** Id редактируемого сотрудника; null — форма создания нового. */
  editingId: number | null = null;
  submitting = false;
  form: FormGroup;

  /** Сотрудник, которому сейчас задаётся новый пароль. */
  passwordMemberId: number | null = null;
  passwordControl = new FormControl<string>('', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]);

  currentUserId: number | null = null;
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private companyService: CompanyService,
    private companyContext: CompanyContextService,
    private languageService: LanguageService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = this.fb.group({
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      companyRole: ['COMPANY_MANAGER', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.currentUserId = this.authService.getUser()?.id ?? null;
    this.subscriptions.add(this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck()));
    this.companyContext.ensureLoaded();
    this.load();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  roleName(role: CompanyRole): string {
    const fallback: Record<CompanyRole, string> = {
      COMPANY_ADMIN: 'Администратор',
      COMPANY_MANAGER: 'Менеджер',
      COMPANY_OPERATOR: 'Оператор'
    };
    return this.t('company.roles.' + role, fallback[role]);
  }

  roleHint(role: CompanyRole): string {
    const fallback: Record<CompanyRole, string> = {
      COMPANY_ADMIN: 'Все права, в том числе управление сотрудниками и реквизитами',
      COMPANY_MANAGER: 'Запросы, ставки и чат',
      COMPANY_OPERATOR: 'Работа со сделкой: статусы, документы, чат'
    };
    return this.t('company.roleHints.' + role, fallback[role]);
  }

  load(): void {
    this.loading = true;
    this.unavailable = false;
    this.error = null;
    this.companyService.getMembers().subscribe({
      next: members => {
        this.members = members;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err.status === 403) {
          this.unavailable = true;
        } else {
          this.error = `${this.errorMessage(err)} (${err.status})`;
        }
        this.cdr.markForCheck();
      }
    });
  }

  openForm(): void {
    this.showForm = true;
    this.success = null;
    this.error = null;
  }

  openEdit(member: CompanyMember): void {
    this.editingId = member.id;
    this.showForm = true;
    this.success = null;
    this.error = null;
    this.form.reset({
      fullName: member.fullName,
      email: member.email,
      phone: member.phone || '',
      password: '',
      companyRole: member.companyRole
    });
    // пароль при редактировании не меняется (есть отдельное «Сменить пароль»)
    this.form.get('password')?.clearValidators();
    this.form.get('password')?.updateValueAndValidity();
    // свою роль менять нельзя: администратор не должен случайно лишить себя прав
    if (member.id === this.currentUserId) {
      this.form.get('companyRole')?.disable();
    } else {
      this.form.get('companyRole')?.enable();
    }
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingId = null;
    this.form.get('password')?.setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(255)]);
    this.form.get('companyRole')?.enable();
    this.form.reset({ fullName: '', email: '', phone: '', password: '', companyRole: 'COMPANY_MANAGER' });
  }

  submit(): void {
    if (this.form.invalid || this.submitting) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    this.error = null;
    const value = this.form.getRawValue();
    if (this.editingId !== null) {
      this.saveEdit(this.editingId, value);
      return;
    }
    this.companyService.createMember({
      fullName: value.fullName,
      email: value.email,
      phone: value.phone || undefined,
      password: value.password,
      companyRole: value.companyRole
    }).subscribe({
      next: member => {
        this.submitting = false;
        this.success = this.t('company.members.created', 'Сотрудник добавлен. Он входит с этим email и заданным паролем.');
        this.cancelForm();
        this.members = [...this.members, member];
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.error = this.errorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  private saveEdit(id: number, value: { fullName: string; email: string; phone: string; companyRole: CompanyRole }): void {
    this.companyService.updateMember(id, {
      fullName: value.fullName,
      email: value.email,
      phone: value.phone || undefined,
      companyRole: value.companyRole
    }).subscribe({
      next: updated => {
        this.submitting = false;
        this.success = this.t('company.members.updated', 'Данные сотрудника сохранены');
        this.members = this.members.map(m => (m.id === updated.id ? updated : m));
        this.cancelForm();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.error = this.errorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  changeRole(member: CompanyMember, role: CompanyRole): void {
    if (role === member.companyRole) {
      return;
    }
    this.error = null;
    this.success = null;
    this.companyService.changeRole(member.id, role).subscribe({
      next: updated => {
        this.members = this.members.map(m => (m.id === updated.id ? updated : m));
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorMessage(err);
        this.load();
      }
    });
  }

  startPassword(member: CompanyMember): void {
    this.passwordMemberId = member.id;
    this.passwordControl.reset('');
    this.success = null;
  }

  savePassword(member: CompanyMember): void {
    if (this.passwordControl.invalid) {
      this.passwordControl.markAsTouched();
      return;
    }
    this.companyService.resetPassword(member.id, this.passwordControl.value || '').subscribe({
      next: () => {
        this.passwordMemberId = null;
        this.success = this.t('company.members.passwordChanged', 'Пароль изменён');
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  async remove(member: CompanyMember): Promise<void> {
    const confirmed = await this.confirmDialog.confirm(
      this.t('company.members.removeMessage', 'Отключить сотрудника? Он больше не сможет войти в систему. Его прошлые ставки и сделки остаются в компании.'),
      { confirmText: this.t('company.members.remove', 'Отключить') }
    );
    if (!confirmed) {
      return;
    }
    this.companyService.removeMember(member.id).subscribe({
      next: () => {
        this.members = this.members.filter(m => m.id !== member.id);
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.error = this.errorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  private errorMessage(err: HttpErrorResponse): string {
    return err.error?.message || this.t('company.members.error', 'Не удалось выполнить действие');
  }
}
